/** Yes + No = $1: a price slider that shows both sides' prices and the implied probabilities (#777). */
import { useId } from 'react';
import { pxToCents } from '../market/format';
import { centsToPx } from '../ticket/order';
import { impliedProbabilityPct, toSidePrice } from '../trading/rules';
import { localizeNumber } from '../i18n/format';
import Msg from '../i18n/Msg';
import { useT } from '../i18n/useT';

export interface ProbabilitySliderProps {
  /** Yes price in whole cents, 1..99. */
  yesCents: number;
  onChange: (cents: number) => void;
}

export default function ProbabilitySlider({ yesCents, onChange }: ProbabilitySliderProps) {
  const id = useId();
  const t = useT();
  const yesPx = centsToPx(String(yesCents)) ?? '0.5';
  const noPx = toSidePrice(yesPx, 1);
  const yesPct = localizeNumber(impliedProbabilityPct(yesPx) ?? '—');
  const noPct = localizeNumber(impliedProbabilityPct(noPx) ?? '—');
  return (
    <div className="space-y-3" data-testid="prob-slider">
      <label htmlFor={id} className="block text-sm font-medium">
        {t('learn.slider.label')}
      </label>
      <input
        id={id}
        type="range"
        min={1}
        max={99}
        step={1}
        value={yesCents}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-valuetext={t('learn.slider.valuetext', { cents: yesCents, pct: yesPct })}
        className="w-full accent-[rgb(var(--accent))]"
      />
      <div className="flex h-9 overflow-hidden rounded-md text-xs font-semibold" aria-hidden="true">
        <div className="flex items-center justify-start bg-yes/25 px-2 text-yes" style={{ width: `${yesCents}%` }}>
          {yesCents >= 12 ? t('learn.slider.bar_yes', { px: pxToCents(yesPx) }) : ''}
        </div>
        <div className="flex flex-1 items-center justify-end bg-no/25 px-2 text-no">
          {yesCents <= 88 ? t('learn.slider.bar_no', { px: pxToCents(noPx) }) : ''}
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-3 text-sm" aria-live="polite">
        <div className="rounded-md bg-surface-2/60 p-3">
          <dt className="text-xs text-muted">{t('common.yes_price')}</dt>
          <dd className="num text-lg font-semibold text-yes" data-testid="slider-yes-px">
            {pxToCents(yesPx)}
          </dd>
          <dd className="text-xs text-muted">
            <Msg k="learn.slider.yes_chance" args={{ pct: <span data-testid="slider-yes-pct">{yesPct}%</span> }} />
          </dd>
        </div>
        <div className="rounded-md bg-surface-2/60 p-3">
          <dt className="text-xs text-muted">{t('learn.slider.no_price')}</dt>
          <dd className="num text-lg font-semibold text-no" data-testid="slider-no-px">
            {pxToCents(noPx)}
          </dd>
          <dd className="text-xs text-muted">
            <Msg k="learn.slider.no_chance" args={{ pct: <span data-testid="slider-no-pct">{noPct}%</span> }} />
          </dd>
        </div>
      </dl>
      <p className="text-xs text-muted">
        {t('learn.slider.sum', { yes: pxToCents(yesPx), no: pxToCents(noPx) })}
      </p>
    </div>
  );
}
